import React, { useEffect, useState, useContext, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContextProvider";
import { ANALYTICS_EVENTS, logAnalyticsEvent } from "../../analytics";
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  Stack,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Popover,
  Divider,
  Checkbox,
  ListItemText,
  Popper,
  Skeleton,
  Radio,
  InputLabel,
  Grid,
  Fade,
} from "@mui/material";

import { BlankPreview } from "../../components/BlankPreview";
import { ReportPreview } from "../../components/ReportPreview";
import nopreview from "../../assets/images/others/no-preview.png";
import {
  useGetInterpretations,
  useSetInterpretationInvite,
  useSetCloseInterpretation,
} from "../../api/interpretation";
import { useGetPeoples } from "../../api/settings";
import moment from "moment";
import { toast } from "sonner";
import { set } from "lodash";
import { Icon } from "@iconify/react/dist/iconify.js";
import { RequireAuth } from "../../RequireAuth";
import Swal from "sweetalert2";
import { SocketContext } from "../../context/SocketContextProvider";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { is } from "date-fns/locale";
import CustomFilters from "../../components/Filters/Filters";
import TypeIcon from "../../components/Reusables/HubSpokeIcon/TypeIcon";

const CustomPopper = (props) => (
  <Popper
    {...props}
    modifiers={[
      {
        name: "offset",
        options: { offset: [0, 6] },
      },
    ]}
    sx={{
      width: 250,
      maxWidth: "fit-content",
      "& .MuiAutocomplete-listbox": {
        maxHeight: 220,
      },
    }}
  />
);

function Interpretations() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { socketMessage } = useContext(SocketContext);
  const [status, setStatus] = useState(() => {
    return sessionStorage.getItem("interpretationStatus") || "requested";
  });
  useEffect(() => {
    sessionStorage.setItem("interpretationStatus", status);
  }, [status]);
  const [interpretationsData, setInterpretationsData] = useState([]);
  const [filters, setFilters] = useState({
    sorting: "desc",
    status: "",
    testResult: "",
    dateFrom: "",
    dateTo: "",
    searchText: "",
    centerIds: "",
    doctorIds: "",
  });
  const queryParams = useMemo(
    () => ({
      status: status || "requested",
      sorting: filters.sorting,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      search: filters.searchText || undefined,
      centers: filters.centerIds ? JSON.parse(filters.centerIds) : undefined,
    }),
    [status, filters],
  );

  const { interpretations, isLoading } = useGetInterpretations(queryParams);

  const [selectedHubIds, setSelectedHubIds] = useState([]);
  const [selectedSpokeIds, setSelectedSpokeIds] = useState([]);

  const { all_centres, selectedCenter } = useContext(AuthContext);

  const handleCustomFilterApply = (cf) => {
    setFilters((prev) => ({
      ...prev,
      sorting: cf.sort || prev.sorting,
      searchText: cf.searchText || "",
      dateFrom: cf.dateFrom || "",
      dateTo: cf.dateTo || "",
      centerIds: JSON.stringify(
        cf.spokeIds?.length > 0 ? cf.spokeIds : cf.hubIds || [],
      ),
    }));

    setSelectedHubIds(cf.hubIds || []);
    setSelectedSpokeIds(cf.spokeIds || []);
  };

  const renderToggle = (props) => <i {...props} className="fa fa-ellipsis-v" />;
  const hasRole = (allowedRoles) => {
    return allowedRoles.includes(user?.userType);
  };

  const hubOptions = useMemo(() => {
    if (!Array.isArray(all_centres)) return [];
    return all_centres.map((hub) => ({
      label: hub.label,
      value: hub.value,
    }));
  }, [all_centres]);

  const spokeOptions = useMemo(() => {
    if (!Array.isArray(all_centres) || selectedHubIds.length === 0) return [];

    return all_centres
      .filter((hub) => selectedHubIds.includes(hub.value)) //  FIX
      .flatMap((hub) =>
        (hub.spokeCenters || []).map((spoke) => ({
          label: spoke.name,
          value: spoke.id,
          hubValue: hub.value, // optional, but correct
        })),
      );
  }, [all_centres, selectedHubIds]);

  const isB2BAdmin = user?.userType === "b2b-admin";
  // useEffect(() => {
  //   if (isB2BAdmin && hubOptions.length > 0) {
  //     const allHubIds = hubOptions.map((h) => h.value);

  //     setSelectedHubIds(allHubIds);
  //     setSelectedSpokeIds([]);

  //     setFilters((prev) => ({
  //       ...prev,
  //       centerIds: JSON.stringify(allHubIds),
  //     }));
  //   }
  // }, [isB2BAdmin, hubOptions]);

  useEffect(() => {
    if (isB2BAdmin && hubOptions.length > 0 && all_centres.length > 0) {
      const allHubIds = all_centres.map((hub) => hub.value);

      const allSpokeIds = all_centres.flatMap((hub) =>
        (hub.spokeCenters || []).map((spoke) => spoke.id),
      );

      setSelectedHubIds(allHubIds);
      setSelectedSpokeIds(allSpokeIds);

      setFilters((prev) => ({
        ...prev,
        centerIds: JSON.stringify([...allHubIds, ...allSpokeIds]),
      }));
    }
  }, [isB2BAdmin, hubOptions, all_centres]);
  useEffect(() => {
    if (
      socketMessage?.activity === "INTERPRETATION" &&
      socketMessage?.report_data
    ) {
      setInterpretationsData((prev) => {
        const updated = [socketMessage.report_data, ...prev];

        return updated;
      });
    } else {
      setInterpretationsData(interpretations);
    }
  }, [isLoading, interpretations, socketMessage]);

  const handleOnDoctorChange = async ({ doctorId, reportId }) => {
    if (doctorId && reportId) {
      const response = await useSetCaseInvite(reportId, { doctors: doctorId });
    }
  };

  const handleCloseCase = async (reportId) => {
    if (reportId) {
      const response = await useSetCloseCase(reportId);
    }
  };
  const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Spandan - Referred Interpretations</title>
      </Helmet>

      <div className="main-wrappers">
        <div
          className="page-wrapper"
          style={{
            maxHeight: "99vh",
            overflowY: "auto",
            paddingBottom: "10px",

            fontFamily: "Figtree",
            backgroundColor: "var(--app-bg)",
          }}
        >
          <div className="content">
            {selectedCenter != null && all_centres.length === 0 && (
              <div
                className="alert alert-warning d-flex justify-content-between align-items-center"
                role="alert"
              >
                <p className="mb-0">
                  <Icon
                    icon="material-symbols:error"
                    width="28"
                    height="28"
                    style={{ color: "var(--app-text)" }}
                  />{" "}
                  &nbsp;&nbsp; You haven't added any centres to your
                  organisation yet.
                </p>
                <Link
                  to="/add-centre"
                  className="dd-login text-decoration-none cursor-pointer"
                >
                  <h6 style={{ fontWeight: "700" }}>Add a Centre</h6>
                </Link>
              </div>
            )}

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                mb: 2,
                fontFamily: "Figtree",
              }}
            >
              Referred Interpretations
            </Typography>

            <Tabs
              value={status}
              onChange={(e, newValue) => {
                setStatus(newValue);
                sessionStorage.setItem("interpretationStatus", newValue);
              }}
              variant="scrollable"
              scrollButtons="auto"
              textColor="inherit"
              indicatorColor="primary"
              TabIndicatorProps={{
                style: {
                  height: "3px",
                  backgroundColor: "#5751E9",
                },
              }}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                // style each Tab; add a 100px gap after each tab except the last one
                "& .MuiTab-root": {
                  color: "var(--app-text)",
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 400,
                  minWidth: "auto",
                  paddingRight: "25px",
                  paddingLeft: "25px",
                  fontFamily: "Figtree",
                },
                "& .Mui-selected": {
                  fontWeight: 700,
                  color: "var(--app-text)",
                },
                "& .MuiTab-root:not(:last-of-type)": {
                  mr: "3%",
                },
              }}
            >
              <Tab label="Requested" value="requested" />
              <Tab label="Interpreted" value="interpreted" />
            </Tabs>

            <CustomFilters
              component="interpretations"
              onApply={handleCustomFilterApply}
            />

            <CaseStatus
              interpretations={interpretations}
              interpretationsData={interpretationsData}
              isLoading={isLoading}
              filters={filters}
              setFilters={setFilters}
              selectedHubIds={selectedHubIds}
              setSelectedHubIds={setSelectedHubIds}
              selectedSpokeIds={selectedSpokeIds}
              setSelectedSpokeIds={setSelectedSpokeIds}
              hubOptions={hubOptions}
              spokeOptions={spokeOptions}
              navigate={navigate}
              user={user}
            />
          </div>
        </div>
      </div>
    </>
  );
}

const CaseStatus = ({
  interpretations,
  interpretationsData,
  isLoading,
  user,
  renderToggle,
  handleOnDoctorChange,
  handleCloseCase,
  navigate,
  filters,
  setFilters,
  selectedHubIds,
  setSelectedHubIds,
  selectedSpokeIds,
  setSelectedSpokeIds,
  hubOptions,
  spokeOptions,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loadingMap, setLoadingMap] = useState({});
  const [openCaseDetails, setOpenCaseDetails] = useState(false);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedDateLabel, setSelectedDateLabel] = useState("Last 7 Days");
  // const { peoples: doctorsList = [], isLoading: doctorsLoading } =
  //   useGetPeoples({
  //     role: "b2b-doctor",
  //     isActiveOnly: false,
  //   });

  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);

  const openDateMenu = Boolean(dateAnchorEl);
  const today = new Date();
  const filterPillSx = {
    minWidth: 150,
    "& .MuiOutlinedInput-root": {
      height: 38,
      borderRadius: "8px",
      backgroundColor: "var(--app-surface)",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
      "& .MuiSelect-select": {
        fontSize: "14px",
        padding: "10px 12px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      color: "var(--app-text)", // optional: smaller label
    },
  };

  const DropdownIcon = (props) => (
    <Icon
      icon="eva:arrow-ios-downward-fill"
      width={18}
      {...props} //  REQUIRED
    />
  );

  useEffect(() => {
    if (selectedSpokeIds.length === 0 && selectedHubIds.length > 0) {
      setFilters((prev) => ({
        ...prev,
        centerIds: JSON.stringify(selectedHubIds),
      }));
    }
  }, [selectedSpokeIds, selectedHubIds]);

  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const defaultFromDate = new Date(startOfToday.getTime() - 6 * 86400000);
  const defaultToDate = startOfToday;

  const addMonths = (date, months) =>
    new Date(date.getFullYear(), date.getMonth() - months, date.getDate());

  const ranges = [
    { label: "Today", from: startOfToday, to: startOfToday },
    {
      label: "Last 7 Days",
      from: new Date(startOfToday.getTime() - 6 * 86400000),
      to: startOfToday,
    },
    {
      label: "Last 30 Days",
      from: new Date(startOfToday.getTime() - 29 * 86400000),
      to: startOfToday,
    },
    {
      label: "Last 3 Months",
      from: addMonths(startOfToday, 3),
      to: new Date(),
    },
    {
      label: "Last 6 Months",
      from: addMonths(startOfToday, 6),
      to: new Date(),
    },
    {
      label: "Last 12 Months",
      from: addMonths(startOfToday, 12),
      to: new Date(),
    },
  ];
  // useEffect(() => {
  //   setFilters((prev) => ({
  //     ...prev,
  //     dateFrom: moment(defaultFromDate).format("DD-MM-YYYY"),
  //     dateTo: moment(defaultToDate).format("DD-MM-YYYY"),
  //   }));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const renderSkeletonRows = (columns = 6) =>
    Array.from({ length: 6 }).map((_, rowIdx) => (
      <TableRow key={`skeleton-${rowIdx}`}>
        {Array.from({ length: columns }).map((_, colIdx) => (
          <TableCell key={colIdx}>
            <Skeleton height={24} />
          </TableCell>
        ))}
      </TableRow>
    ));

  // const hasOpenCases = casesData.some((c) => c.status !== "closed");

  // const renderIcon = (type) => {
  //   if (type?.toLowerCase() === "hub")
  //     return <Icon icon="mingcute:hospital-fill" width="15" height="15" color="var(--app-text)" />;
  //   if (type?.toLowerCase() === "spoke")
  //     return <Icon icon="icon-park-solid:hospital-three" width="15" height="15" color="var(--app-text)" />;
  //   return null;
  // };

  const downloadCSV = (rows, filename = "referred-cases.csv") => {
    if (!rows.length) return;

    const escape = (v) =>
      `"${String(v ?? "")
        .replace(/"/g, '""')
        .replace(/\n/g, " ")}"`;

    const headers = Object.keys(rows[0]).join(",");
    const csvBody = rows
      .map((row) => Object.values(row).map(escape).join(","))
      .join("\n");

    const csv = `${headers}\n${csvBody}`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  };

  // const handleExportCSV = async () => {
  //   try {
  //     toast.loading("Preparing CSV...");
  //     console.log("filters", filters);

  //     const res = await exportCases({
  //       status,
  //       sorting: filters.sorting,
  //       dateFrom: filters.dateFrom || undefined,
  //       dateTo: filters.dateTo || undefined,
  //       search: filters.searchText || undefined,
  //       centers: filters.centerIds
  //         ? JSON.parse(filters.centerIds)
  //         : undefined,
  //       doctorIds: filters.doctorIds
  //         ? JSON.parse(filters.doctorIds)
  //         : undefined,
  //       export: true,
  //     });

  //     const rows = (res?.data || []).map((c) => ({
  //       "Report ID": c.report_id || c.collection_id,
  //       "Referred On": c.referred_on
  //         ? moment(c.referred_on).format("DD/MM/YYYY HH:mm")
  //         : "",
  //       "Referred From": c.referred_by_center_name,
  //       "Referred To": c.referred_to_center_name,
  //       Patient: c.patient_name,
  //       Doctor:
  //         (c.doctors || []).map((d) => d.fullName).join(", "),
  //       Status: c.status,
  //     }));

  //     if (!rows.length) {
  //       toast.dismiss();
  //       toast.info("No data to export");
  //       return;
  //     }

  //     downloadCSV(
  //       rows,
  //       `referred-cases-${moment().format("YYYY-MM-DD")}.csv`
  //     );

  //     toast.dismiss();
  //     toast.success("CSV downloaded");
  //   } catch (err) {
  //     toast.dismiss();
  //     console.error(err);
  //     toast.error("Failed to export CSV");
  //   }
  // };

  // const showEmptyState = !isLoading && tableCases.length === 0;

  const visibleInterpretations =
    interpretationsData.length > 0
      ? interpretationsData
      : Array.isArray(interpretations)
        ? interpretations
        : [];
  const showEmptyState =
    !isLoading &&
    Array.isArray(interpretations) &&
    visibleInterpretations.length === 0;

  // check if any open case exists to decide if ACTION column should show
  const hasOpenInterpretations = visibleInterpretations.some(
    (c) => c.status !== "interpreted",
  );

  const isInterpretedHeader =
    visibleInterpretations?.[0]?.status === "interpreted";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 0,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        {/* RIGHT ACTION */}
        {/* <Button
          startIcon={<Icon icon="material-symbols:download" width="20px" height="20px" />}
          sx={{ textTransform: "none", fontWeight: 700,color: "#5751E9" ,fontSize: "15px" }}
          onClick={handleExportCSV}
        >
          Export CSV
        </Button> */}
      </Box>
      {showEmptyState ? (
        <Box sx={{ mt: 6 }}>
          <BlankPreview
            icon={nopreview}
            title="No Referred Interpretations Available"
            sx={{ backgroundColor: "var(--app-surface-2)" }}
          />
        </Box>
      ) : (
        <Fade in={true}>
          <TableContainer
            className="app-table-card"
            component={Paper}
            elevation={0}
          >
            <Table
              sx={{
                borderCollapse: "separate",
                borderSpacing: "0 10px", // <-- adds 10px gap between rows
                backgroundColor: "var(--app-surface-2)",
                "& .MuiTableCell-root": {
                  borderBottom: "none",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "transparent",
                    borderRight: "none",
                    fontFamily: "Figtree",
                  }}
                >
                  {" "}
                  {/* transparent header */}
                  {[
                    "REPORT ID",
                    isInterpretedHeader ? "INTERPRETED ON" : "REFERRED ON",
                    "PATIENT",
                    "REFERRED FROM",
                    "REFERRED TO",
                    "STATUS",
                    "ACTION",
                  ].map((head, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        fontWeight: 800,
                        fontSize: "15px",
                        color: "#A3A3A3",
                        py: 1.5,
                        whiteSpace: "nowrap",
                        fontFamily: "Figtree",
                        backgroundColor: "var(--app-surface-2)",
                        borderBottom: "none",
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading
                  ? renderSkeletonRows(7)
                  : visibleInterpretations.map((caseDt, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: "var(--app-surface)",
                        borderRadius: "8px",
                        "&:last-child td, &:last-child th": { border: 0 },
                        "& td:first-of-type": {
                          borderTopLeftRadius: "8px",
                          borderBottomLeftRadius: "8px",
                        },
                        "& td": { py: "10px", fontSize: "14px" },
                        "& td:last-of-type": {
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                        },
                      }}
                    >
                      <TableCell
                        onClick={() =>
                          navigate(`/ecg-reports/${caseDt?.report_id}`)
                        }
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: "15px",
                        }}
                      >
                        {caseDt?.report_id || "--"}
                      </TableCell>

                      <TableCell sx={{ fontSize: "15px", py: 1.2 }}>
                        {moment(caseDt.referred_on).format(
                          "DD/MM/YYYY, hh:mm a",
                        )}
                      </TableCell>
                      <TableCell>
                        {caseDt.patient_id ? (
                          <Link
                            to={`/patients/${caseDt.patient_id}`}
                            style={{
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: "15px",
                              color: "var(--app-text)",
                            }}
                          >
                            {caseDt.patient_name
                              ? caseDt.patient_name.length > 10
                                ? caseDt.patient_name.slice(0, 10) + "..."
                                : caseDt.patient_name
                              : "--"}
                          </Link>
                        ) : (
                          <span
                            style={{
                              fontSize: "15px",
                              color: "var(--app-text)",
                            }}
                          >
                            {caseDt.patient_name
                              ? caseDt.patient_name.length > 10
                                ? caseDt.patient_name.slice(0, 10) + "..."
                                : caseDt.patient_name
                              : "--"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "15px", py: 1.2 }}>
                        <Box display="flex" alignItems="center">
                          <TypeIcon
                            type={caseDt.referred_by_center_type}
                            size={15}
                          />
                          <Typography ml={1}>
                            {caseDt.referred_by_center_name
                              ? caseDt.referred_by_center_name.slice(0, 15)
                              : "--"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "15px", py: 1.2 }}>
                        <Box display="flex" alignItems="center">
                          <TypeIcon
                            type={caseDt.referred_to_center_type}
                            size={15}
                          />
                          <Typography ml={1}>
                            {caseDt.referred_to_center_name
                              ? caseDt.referred_to_center_name.slice(0, 15)
                              : "--"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "15px",
                          py: 1.2,
                          textTransform: "capitalize",
                        }}
                      >
                        <Chip
                          label={caseDt.status}
                          sx={{
                            backgroundColor:
                              caseDt.status === "interpreted"
                                ? "#E8F5E9"
                                : "#FFF8E1",
                            color:
                              caseDt.status === "interpreted"
                                ? "#2E7D32"
                                : "#F57F17",
                            fontWeight: 600,
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Button
                          variant="contained"
                          onClick={() => {
                            logAnalyticsEvent(
                              caseDt.status === "interpreted"
                                ? ANALYTICS_EVENTS.IN_HOUSE_INTERPRETATION_EDIT
                                : ANALYTICS_EVENTS.IN_HOUSE_INTERPRET_CLICKED,
                              { report_id: caseDt?.report_id }
                            );
                            navigate(`/interpretation/${caseDt?.report_id}`);
                          }}
                          startIcon={
                            <Icon
                              icon={
                                caseDt.status === "interpreted"
                                  ? "material-symbols:edit"
                                  : "icon-park-solid:table-report"
                              }
                              width={20}
                              height={20}
                            />
                          }
                          sx={{
                            backgroundColor: "#5751E9",
                            color: "#fff",
                            boxShadow: "none",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            borderRadius: "8px",
                            px: 2.5,
                            py: 1,
                            "&:hover": { backgroundColor: "#4842d5" },
                          }}
                        >
                          {caseDt.status === "interpreted"
                            ? "Edit"
                            : "Interpret"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Fade>
      )}
    </>
  );
};
const SelectDoctorUI = ({ centerId, reportId, doctors, onChange }) => {
  const { peoples, isLoading: isLoadingPeople } = useGetPeoples({
    role: "b2b-doctor",
    centerIds: [centerId],
  });
  const [doctorsMember, setDoctorsMember] = useState(null);
  const [defaultValue, setDefaultValue] = useState([]);
  useEffect(() => {
    if (doctors?.length) {
      // extract _id only
      setDefaultValue(doctors.map((d) => d._id));
    }
  }, [doctors]);
  useEffect(() => {
    if (!isLoadingPeople) {
      setDoctorsMember(peoples);
    }
  }, [isLoadingPeople]);

  return (
    <>
      {doctorsMember && defaultValue && (
        <VStack>
          <CheckPicker
            data={doctorsMember || []}
            defaultValue={defaultValue}
            valueKey="_id"
            labelKey="fullName"
            onChange={(id) => {
              onChange({ doctorId: id, reportId });
            }}
            placeholder="Select without search"
          />
        </VStack>
      )}
    </>
  );
};

export default Interpretations;
